import {
    ApplicationConfig,
    importProvidersFrom,
    provideAppInitializer,
    provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter } from "@angular/router";
import { routes } from "./app.routes";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { DATE_PIPE_DEFAULT_OPTIONS, LocationStrategy, registerLocaleData } from "@angular/common";
import localeId from "@angular/common/locales/id";
import { NgxSpinnerModule } from "ngx-spinner";
import { PathPreserveQueryLocationStrategy } from "./path-preserve-query-location-strategy";
import { MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS } from "@angular/material/progress-spinner";
import { MAT_SELECT_CONFIG } from "@angular/material/select";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { provideNgxMask } from "ngx-mask";
import { errorInterceptor } from "./shared/interceptors/error.interceptor";
import { authInterceptor } from "./shared/interceptors/auth.interceptor";
import { environment } from "../environments/environment";
import { provideTranslation } from "@policystreet/localization";
import { provideDateLocaleSync } from "@core/i18n/date-locale-sync";
import { marketFor } from "./shared/market/market.token";

// Indonesian date names (LocalizedDatePipe) and Rupiah grouping/decimal separators (ID_MARKET.currency).
registerLocaleData(localeId, "id");

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        // Load reCAPTCHA Enterprise at startup using the per-environment site key, so the
        // preloaded key always matches the one RecaptchaService passes to execute().
        provideAppInitializer(() => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.siteKey}`;
            script.async = true;
            document.head.appendChild(script);
        }),
        provideRouter(routes),
        provideNgxMask(),
        provideAnimationsAsync(),
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "fill", subscriptSizing: "dynamic" } },
        { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: true, autoFocus: false, width: 328 } },
        { provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS, useValue: { diameter: 50 } },
        { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: "dd MMM yyyy" } },
        provideMomentDateAdapter(
            {
                parse: {
                    dateInput: ["l", "LL"],
                },
                display: {
                    dateInput: "MMM D, YYYY",
                    monthYearLabel: "MMM YYYY",
                    dateA11yLabel: "LL",
                    monthYearA11yLabel: "MMMM YYYY",
                },
            },
            { strict: true },
        ),
        // Malaysia: English only. Indonesia: English and Bahasa Indonesia (see MarketProfile.languages).
        provideTranslation({ storageKey: "tts_ui_language", languages: [...marketFor(environment.country).languages] }),
        provideDateLocaleSync(),
        importProvidersFrom(NgxSpinnerModule),
        provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
        { provide: LocationStrategy, useClass: PathPreserveQueryLocationStrategy },
    ],
};
